import { createRef, MutableRefObject, RefObject, useCallback, useEffect, useRef, useState, MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import { useDraggableScroll } from '../../hooks/useDraggableScroll';
import House from './House';
import TabContents from './TabContents';
import TabUnderline from './TabUnderline';

export const tabItems = [
  { name: '거실, 방' },
  { name: '주방' },
  { name: '화장실' },
  { name: '현관, 쓰레기배출' },
  { name: '세탁, 실내창틀, 베란다' },
  { name: '미제공 서비스' },
] as const;

export default function ServiceRangeSection() {
  const tabMenuRef = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement>;
  const tabRefs = tabItems.reduce((acc: { [name: string]: RefObject<any> }, item) => {
    acc[item.name] = createRef();
    return acc;
  }, {});
  const { events } = useDraggableScroll(tabMenuRef);

  const [activeTab, setActiveTab] = useState<typeof tabItems[number]['name']>('거실, 방');
  const [isTabAnimating, setIsTabAnimating] = useState(false);

  const clickTab = (
    event: MouseEvent<HTMLElement, globalThis.MouseEvent>,
    tabName: typeof tabItems[number]['name'],
  ) => {
    setActiveTab(tabName);
    setIsTabAnimating(true);
    event.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  return (
    <Container>
      <OuterWrapper>
        <InnerWrapper>
          <Bullet>서비스 범위</Bullet>
          <TitleWrapper>
            <FakeTitle>이 모든 집안일이 예약 한번에 끝!</FakeTitle>
            <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
            <TitleTablet>{TitleTabletSvg}</TitleTablet>
            <TitleMobile>{TitleMobileSvg}</TitleMobile>
          </TitleWrapper>
          <House activeTab={activeTab} />
        </InnerWrapper>
      </OuterWrapper>

      <TabContianer>
        <TabMenu ref={tabMenuRef} {...events}>
          <TabMenuOuterWrapper>
            <TabMenuInnerWrapper>
              <TabMenuList>
                {tabItems.map((item) => (
                  <TabItem
                    key={item.name}
                    $isActive={activeTab === item.name}
                    $isAnimating={isTabAnimating}
                    onClick={(e) => clickTab(e, item.name)}
                    ref={tabRefs[item.name]}
                  >
                    {item.name}
                  </TabItem>
                ))}
                <TabUnderline
                  refs={tabRefs}
                  activeTabName={activeTab}
                  isAnimating={isTabAnimating}
                  finishAnimating={() => setIsTabAnimating(false)}
                />
              </TabMenuList>
            </TabMenuInnerWrapper>
          </TabMenuOuterWrapper>
        </TabMenu>
        <TabContents activeTab={activeTab} />
      </TabContianer>
    </Container>
  );
}

const Container = styled.section`
  width: 100%;
`;

const OuterWrapper = styled.div`
  position: relative;
  padding: 0 24px;
  padding-top: 128px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 80px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-top: 90px;
  }
`;

const InnerWrapper = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 64px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-bottom: 50px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 50px;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }
`;

const TitleMobile = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: block;
  }
`;

const TabContianer = styled.div``;

const Bullet = styled.h2`
  display: block;
  width: fit-content;
  font-weight: 600;
  font-size: 21px;
  line-height: 140%;
  color: #00bed6;
  padding: 12px 26px;
  border: 2px solid #00bed6;
  border-radius: 70px;
  margin: 0 auto;
  margin-bottom: 38px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 8px 18px;
    border: 1.5px solid #00bed6;
    border-radius: 50px;
    margin-bottom: 20px;
    font-size: 16px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 8px 16px;
    margin-bottom: 24px;
    font-size: 14px;
  }
`;

const TabMenu = styled.div`
  border-bottom: 1px solid #cccccc;
  padding: 0 24px;
  padding-top: 20px;
  padding-bottom: 20px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 16px;
    padding-bottom: 16px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-top: 13px;
    padding-bottom: 13px;
  }

  overflow-x: scroll;
  overflow-y: hidden;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
    height: 0;
    width: 0;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background-color: gray;
  }
  &::-webkit-scrollbar-button {
    width: 0;
    height: 0;
  }
`;

const TabMenuOuterWrapper = styled.div`
  margin: 0 auto;

  /* width: fit-content; */
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }
`;

const TabMenuInnerWrapper = styled.div`
  /* padding: 0 24px; */
  width: fit-content;
`;

const TabMenuList = styled.ul`
  display: flex;
  flex-direction: row;
  position: relative;
  list-style: none;
  margin: 0 auto;
  position: relative;
`;

const TabItem = styled.li<{ $isActive: boolean; $isAnimating: boolean }>`
  margin-right: 60px;
  font-weight: 300;
  font-size: 22px;
  line-height: 140%;
  color: rgba(0, 0, 0, 0.4);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  position: relative;

  ${({ $isActive }) =>
    $isActive &&
    css`
      color: rgba(0, 0, 0, 0.85);
      font-weight: 600;
    `}

  &:last-child {
    margin-right: 0;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: ${({ $isActive, $isAnimating }) => ($isActive && !$isAnimating ? '3px' : '0')};
    background: #000000;
    bottom: -20px;

    @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
      bottom: -16px;
    }

    @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
      bottom: -13px;
    }
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 21px;
    margin-right: 50px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 19px;
    margin-right: 40px;
    font-weight: 400;
  }
`;

const TitleDesktopSvg = (
  <svg width="606" height="44" viewBox="0 0 606 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.792155 14.558C0.792155 21.596 5.66816 26.334 12.3842 26.334C19.0542 26.334 23.9762 21.596 23.9762 14.558C23.9762 7.52 19.0542 2.736 12.3842 2.736C5.66816 2.736 0.792155 7.52 0.792155 14.558ZM5.99016 14.512C5.99016 10.05 8.65816 7.336 12.3842 7.336C16.0182 7.336 18.7782 10.05 18.7782 14.512C18.7782 18.974 16.0642 21.688 12.3842 21.688C8.65816 21.688 5.99016 18.974 5.99016 14.512ZM30.4162 38.57H35.5222V0.251998H30.4162V38.57ZM80.4731 17.364H64.4191V6.508H80.4731V17.364ZM91.8351 34.43V30.106H74.9991V21.78H85.5791V2.23H59.3591V21.78H69.9851V30.106H53.1031V34.43H91.8351ZM133.232 25.414V20.998H94.4995V25.414H133.232ZM100.94 29.002V42.572H127.482V38.202H106.092V29.002H100.94ZM126.976 17.502V13.178H106V5.818H126.654V1.402H100.894V17.502H126.976ZM179.057 39.352H164.337V34.476H179.057V39.352ZM179.057 23.16H184.163V0.159999H179.057V23.16ZM159.001 6.416C158.955 9.498 158.081 12.12 156.287 14.236C154.493 16.398 152.055 18.008 148.973 19.112L151.365 23.298C155.965 21.458 160.059 18.146 161.715 14.052C163.739 18.054 167.787 21.136 172.249 22.608L174.733 18.468C171.559 17.456 169.029 15.938 167.143 13.914C165.349 11.936 164.383 9.406 164.337 6.416H172.663V2.046H150.307V6.416H159.001ZM184.163 43.722V25.46H179.057V30.198H164.337V25.46H159.231V43.722H184.163ZM223.627 42.48V38.064H203.433V27.346H198.373V42.48H223.627ZM189.909 12.856C189.909 18.79 194.693 23.252 201.179 23.252C207.711 23.252 212.541 18.79 212.541 12.856C212.541 7.06 207.711 2.46 201.179 2.46C194.693 2.46 189.909 7.06 189.909 12.856ZM195.061 12.81C195.061 9.406 197.637 6.922 201.179 6.922C204.675 6.922 207.435 9.406 207.435 12.81C207.435 16.168 204.675 18.836 201.179 18.836C197.637 18.836 195.061 16.168 195.061 12.81ZM229.607 15.386V11.016H222.569V0.205998H217.463V30.06H222.569V15.386H229.607ZM232.179 11.384C232.179 16.996 236.825 21.412 243.311 21.412C249.935 21.412 254.627 16.996 254.627 11.384C254.627 5.588 249.935 1.31 243.311 1.31C236.825 1.31 232.179 5.588 232.179 11.384ZM237.285 11.338C237.285 8.072 239.769 5.634 243.311 5.634C246.899 5.634 249.521 8.072 249.521 11.338C249.521 14.604 246.899 16.996 243.311 16.996C239.769 16.996 237.285 14.604 237.285 11.338ZM267.001 21.918V0.159999H261.895V21.918H267.001ZM267.645 43.952V39.582H246.715V36.316H267.001V24.172H241.655V28.588H261.895V31.992H241.655V43.952H267.645ZM273.116 14.558C273.116 21.596 277.992 26.334 284.708 26.334C291.378 26.334 296.3 21.596 296.3 14.558C296.3 7.52 291.378 2.736 284.708 2.736C277.992 2.736 273.116 7.52 273.116 14.558ZM278.314 14.512C278.314 10.05 280.982 7.336 284.708 7.336C288.342 7.336 291.102 10.05 291.102 14.512C291.102 18.974 288.388 21.688 284.708 21.688C280.982 21.688 278.314 18.974 278.314 14.512ZM302.74 38.57H307.846V0.251998H302.74V38.57ZM345.897 14.742C345.897 13.684 345.805 12.718 345.575 11.752H348.933V17.64H345.621C345.805 16.766 345.897 15.8 345.897 14.742ZM330.119 14.696C330.119 10.142 332.465 7.52 335.547 7.52C338.721 7.52 340.975 10.142 340.975 14.696C340.975 19.25 338.721 21.78 335.547 21.78C332.465 21.78 330.119 19.25 330.119 14.696ZM357.259 38.616H362.273V0.251998H357.259V38.616ZM353.855 37.144V0.803999H348.933V7.06H343.597C343.413 6.784 343.183 6.508 342.953 6.278C341.159 4.254 338.629 3.012 335.547 3.012C329.613 3.012 325.243 7.75 325.243 14.742C325.243 21.734 329.613 26.38 335.547 26.38C338.629 26.38 341.159 25.23 342.953 23.206C343.183 22.93 343.413 22.654 343.597 22.378H348.933V37.144H353.855ZM372.159 12.028C372.159 8.808 374.643 6.278 378.093 6.278C381.589 6.278 384.027 8.808 384.027 12.028C384.027 15.294 381.589 17.916 378.093 17.916C374.643 17.916 372.159 15.294 372.159 12.028ZM400.219 43.446V26.702H374.965V31.072H395.113V43.446H400.219ZM406.567 18.79V14.466H400.219V10.004H406.567V5.68H400.219V0.159999H395.113V24.586H400.219V18.79H406.567ZM367.053 12.12C367.053 17.87 371.653 22.286 378.093 22.286C384.487 22.286 389.133 17.87 389.133 12.12C389.133 6.324 384.487 1.862 378.093 1.862C371.653 1.862 367.053 6.324 367.053 12.12ZM454.738 42.986V38.616H434.406V31.072H429.3V42.986H454.738ZM444.664 11.936V7.612H420.192V11.936H444.664ZM438.546 5.358V0.941997H426.816V5.358H438.546ZM437.304 21.366C437.304 23.436 435.556 25.046 432.796 25.046C430.036 25.046 428.196 23.436 428.196 21.366C428.196 19.296 430.036 17.686 432.796 17.686C435.556 17.686 437.304 19.296 437.304 21.366ZM442.272 21.32C442.272 16.628 438.132 13.546 432.796 13.546C427.414 13.546 423.09 16.628 423.09 21.32C423.09 26.15 427.414 29.048 432.796 29.048C437.994 29.048 442.272 26.15 442.272 21.32ZM460.488 17.594V13.132H453.542V0.205998H448.482V32.222H453.542V17.594H460.488ZM497.929 30.29V0.205998H492.777V10.234H483.945V2.046H478.931V8.256H468.581V2.046H463.475V23.068H483.945V14.604H492.777V30.29H497.929ZM498.527 42.434V38.018H478.149V27.714H472.997V42.434H498.527ZM478.931 18.698H468.581V12.58H478.931V18.698ZM507.953 14.696C507.953 10.464 510.299 7.888 513.289 7.888C516.417 7.888 518.671 10.464 518.671 14.696C518.671 18.974 516.417 21.458 513.289 21.458C510.299 21.458 507.953 18.974 507.953 14.696ZM534.771 38.616H539.785V0.251998H534.771V38.616ZM503.077 14.742C503.077 21.596 507.355 26.104 513.289 26.104C517.935 26.104 521.293 23.528 522.857 19.434C523.179 18.56 523.409 17.594 523.547 16.582H526.583V37.144H531.459V0.803999H526.583V12.212H523.409C523.271 11.476 523.087 10.786 522.857 10.096C521.293 5.91 517.889 3.38 513.289 3.38C507.355 3.38 503.077 7.888 503.077 14.742ZM588.508 36.454V32.038H567.532V28.956H588.692V24.54H562.564V43.676H589.244V39.352H567.532V36.454H588.508ZM560.264 1.632V5.818H568.498C568.498 8.44 567.992 11.614 567.164 14.558C566.98 15.248 566.796 15.892 566.566 16.536H556.354V20.906H595.086V16.536H588.462C588.646 15.938 588.784 15.294 588.922 14.604C589.75 10.74 589.98 5.726 589.888 1.632H575.95V5.818H584.736C584.828 8.578 584.506 11.936 583.862 14.742L583.402 16.536H571.718C571.948 15.892 572.132 15.202 572.27 14.512C573.19 10.51 573.742 5.864 573.65 1.632H560.264ZM599.866 37.696H605.524V32.268H599.866V37.696ZM600.786 27.024H604.604L605.57 1.126H599.958L600.786 27.024Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="366" height="27" viewBox="0 0 366 27" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.814625 9.121C0.814625 13.252 3.67663 16.033 7.61863 16.033C11.5336 16.033 14.4226 13.252 14.4226 9.121C14.4226 4.99 11.5336 2.182 7.61863 2.182C3.67663 2.182 0.814625 4.99 0.814625 9.121ZM3.86563 9.094C3.86563 6.475 5.43163 4.882 7.61863 4.882C9.75163 4.882 11.3716 6.475 11.3716 9.094C11.3716 11.713 9.77863 13.306 7.61863 13.306C5.43163 13.306 3.86563 11.713 3.86563 9.094ZM18.2026 23.215H21.1996V0.723999H18.2026V23.215ZM48.6639 10.768H39.2409V4.396H48.6639V10.768ZM55.3329 20.785V18.247H45.4509V13.36H51.6609V1.885H36.2709V13.36H42.5079V18.247H32.5989V20.785H55.3329ZM80.1708 15.493V12.901H57.4368V15.493H80.1708ZM61.2168 17.599V25.564H76.7958V22.999H64.2408V17.599H61.2168ZM76.4988 10.849V8.311H64.1868V3.991H76.3098V1.399H61.1898V10.849H76.4988ZM108.148 23.674H99.5081V20.812H108.148V23.674ZM108.148 14.17H111.145V0.669999H108.148V14.17ZM96.3761 4.342C96.3491 6.151 95.8361 7.69 94.7831 8.932C93.7301 10.201 92.2991 11.146 90.4901 11.794L91.8941 14.251C94.5941 13.171 96.9971 11.227 97.9691 8.824C99.1571 11.173 101.533 12.982 104.152 13.846L105.61 11.416C103.747 10.822 102.262 9.931 101.155 8.743C100.102 7.582 99.5351 6.097 99.5081 4.342H104.395V1.777H91.2731V4.342H96.3761ZM111.145 26.239V15.52H108.148V18.301H99.5081V15.52H96.5111V26.239H111.145ZM134.849 25.51V22.918H122.996V16.627H120.026V25.51H134.849ZM115.058 8.122C115.058 11.605 117.866 14.224 121.673 14.224C125.507 14.224 128.342 11.605 128.342 8.122C128.342 4.72 125.507 2.02 121.673 2.02C117.866 2.02 115.058 4.72 115.058 8.122ZM118.082 8.095C118.082 6.097 119.594 4.639 121.673 4.639C123.725 4.639 125.345 6.097 125.345 8.095C125.345 10.066 123.725 11.632 121.673 11.632C119.594 11.632 118.082 10.066 118.082 8.095ZM138.359 9.607V7.042H134.228V0.696999H131.231V18.22H134.228V9.607H138.359ZM140.409 7.258C140.409 10.552 143.136 13.144 146.943 13.144C150.831 13.144 153.585 10.552 153.585 7.258C153.585 3.856 150.831 1.345 146.943 1.345C143.136 1.345 140.409 3.856 140.409 7.258ZM143.406 7.231C143.406 5.314 144.864 3.883 146.943 3.883C149.049 3.883 150.588 5.314 150.588 7.231C150.588 9.148 149.049 10.552 146.943 10.552C144.864 10.552 143.406 9.148 143.406 7.231ZM160.848 13.441V0.669999H157.851V13.441H160.848ZM161.226 26.374V23.809H148.941V21.892H160.848V14.764H145.971V17.356H157.851V19.354H145.971V26.374H161.226ZM164.977 9.121C164.977 13.252 167.839 16.033 171.781 16.033C175.696 16.033 178.585 13.252 178.585 9.121C178.585 4.99 175.696 2.182 171.781 2.182C167.839 2.182 164.977 4.99 164.977 9.121ZM168.028 9.094C168.028 6.475 169.594 4.882 171.781 4.882C173.914 4.882 175.534 6.475 175.534 9.094C175.534 11.713 173.941 13.306 171.781 13.306C169.594 13.306 168.028 11.713 168.028 9.094ZM182.365 23.215H185.362V0.723999H182.365V23.215ZM208.776 9.229C208.776 8.608 208.722 8.041 208.587 7.474H210.558V10.93H208.614C208.722 10.417 208.776 9.85 208.776 9.229ZM199.515 9.202C199.515 6.529 200.892 4.99 202.701 4.99C204.564 4.99 205.887 6.529 205.887 9.202C205.887 11.875 204.564 13.36 202.701 13.36C200.892 13.36 199.515 11.875 199.515 9.202ZM215.445 23.242H218.388V0.723999H215.445V23.242ZM213.447 22.378V1.048H210.558V4.72H207.426C207.318 4.558 207.183 4.396 207.048 4.261C205.995 3.073 204.51 2.344 202.701 2.344C199.218 2.344 196.653 5.125 196.653 9.229C196.653 13.333 199.218 16.06 202.701 16.06C204.51 16.06 205.995 15.385 207.048 14.197C207.183 14.035 207.318 13.873 207.426 13.711H210.558V22.378H213.447ZM224.731 7.636C224.731 5.746 226.189 4.261 228.214 4.261C230.266 4.261 231.697 5.746 231.697 7.636C231.697 9.553 230.266 11.092 228.214 11.092C226.189 11.092 224.731 9.553 224.731 7.636ZM241.201 26.077V16.249H226.378V18.814H238.204V26.077H241.201ZM244.927 11.605V9.067H241.201V6.448H244.927V3.91H241.201V0.669999H238.204V15.007H241.201V11.605H244.927ZM221.734 7.69C221.734 11.065 224.434 13.657 228.214 13.657C231.967 13.657 234.694 11.065 234.694 7.69C234.694 4.288 231.967 1.669 228.214 1.669C224.434 1.669 221.734 4.288 221.734 7.69ZM274.281 25.807V23.242H262.347V18.814H259.35V25.807H274.281ZM268.368 7.582V5.044H254.004V7.582H268.368ZM264.777 3.721V1.129H257.892V3.721H264.777ZM264.048 13.117C264.048 14.332 263.022 15.277 261.402 15.277C259.782 15.277 258.702 14.332 258.702 13.117C258.702 11.902 259.782 10.957 261.402 10.957C263.022 10.957 264.048 11.902 264.048 13.117ZM266.964 13.09C266.964 10.336 264.534 8.527 261.402 8.527C258.243 8.527 255.705 10.336 255.705 13.09C255.705 15.925 258.243 17.626 261.402 17.626C264.453 17.626 266.964 15.925 266.964 13.09ZM277.656 10.903V8.284H273.579V0.696999H270.609V19.489H273.579V10.903H277.656ZM300.172 18.355V0.696999H297.148V6.583H291.964V1.777H289.021V5.422H282.946V1.777H279.949V14.116H291.964V9.148H297.148V18.355H300.172ZM300.523 25.483V22.891H288.562V16.843H285.538V25.483H300.523ZM289.021 11.551H282.946V7.96H289.021V11.551ZM306.596 9.202C306.596 6.718 307.973 5.206 309.728 5.206C311.564 5.206 312.887 6.718 312.887 9.202C312.887 11.713 311.564 13.171 309.728 13.171C307.973 13.171 306.596 11.713 306.596 9.202ZM322.337 23.242H325.28V0.723999H322.337V23.242ZM303.734 9.229C303.734 13.252 306.245 15.898 309.728 15.898C312.455 15.898 314.426 14.386 315.344 11.983C315.533 11.47 315.668 10.903 315.749 10.309H317.531V22.378H320.393V1.048H317.531V7.744H315.668C315.587 7.312 315.479 6.907 315.344 6.502C314.426 4.045 312.428 2.56 309.728 2.56C306.245 2.56 303.734 5.206 303.734 9.229ZM354.958 21.973V19.381H342.646V17.572H355.066V14.98H339.73V26.212H355.39V23.674H342.646V21.973H354.958ZM338.38 1.534V3.991H343.213C343.213 5.53 342.916 7.393 342.43 9.121C342.322 9.526 342.214 9.904 342.079 10.282H336.085V12.847H358.819V10.282H354.931C355.039 9.931 355.12 9.553 355.201 9.148C355.687 6.88 355.822 3.937 355.768 1.534H347.587V3.991H352.744C352.798 5.611 352.609 7.582 352.231 9.229L351.961 10.282H345.103C345.238 9.904 345.346 9.499 345.427 9.094C345.967 6.745 346.291 4.018 346.237 1.534H338.38ZM362.165 22.702H365.486V19.516H362.165V22.702ZM362.705 16.438H364.946L365.513 1.237H362.219L362.705 16.438Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleMobileSvg = (
  <svg width="181" height="65" viewBox="0 0 181 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.221309 9.148C0.221309 13.279 3.08331 16.033 6.99831 16.033C10.9673 16.033 13.8023 13.279 13.8023 9.148C13.8023 4.99 10.9673 2.236 6.99831 2.236C3.08331 2.236 0.221309 4.99 0.221309 9.148ZM3.51531 9.121C3.51531 6.691 5.00031 5.152 6.99831 5.152C9.02331 5.152 10.4813 6.691 10.4813 9.121C10.4813 11.578 9.02331 13.09 6.99831 13.09C5.00031 13.09 3.51531 11.578 3.51531 9.121ZM17.2313 23.107H20.4983V0.831999H17.2313V23.107ZM46.6116 10.687H37.9716V4.72H46.6116V10.687ZM53.5506 20.785V18.031H43.9656V13.495H49.9326V1.966H34.6506V13.495H40.6446V18.031H31.0326V20.785H53.5506ZM77.8885 15.601V12.793H55.3705V15.601H77.8885ZM59.0965 17.464V25.456H74.5675V22.675H62.4175V17.464H59.0965ZM74.2705 11.038V8.23H62.3905V4.288H74.0815V1.507H59.0695V11.038H74.2705ZM104.65 23.377H96.6037V20.785H104.65V23.377ZM104.65 14.17H107.917V0.804999H104.65V14.17ZM101.221 4.612V1.831H88.1257V4.612H93.0667C92.9047 7.906 90.7717 10.444 87.3427 11.632L88.8547 14.305C91.3927 13.333 93.8767 11.362 94.8217 9.04C95.8477 11.335 98.5207 13.117 100.87 13.9L102.436 11.281C98.7907 10.201 96.6577 7.879 96.4957 4.612H101.221ZM107.917 26.131V15.412H104.65V18.031H96.6037V15.412H93.3367V26.131H107.917ZM131.148 25.375V22.54H119.646V16.519H116.325V25.375H131.148ZM111.438 8.203C111.438 11.686 114.165 14.305 118.053 14.305C121.914 14.305 124.695 11.686 124.695 8.203C124.695 4.774 121.914 2.074 118.053 2.074C114.165 2.074 111.438 4.774 111.438 8.203ZM114.732 8.176C114.732 6.313 116.163 4.936 118.053 4.936C119.943 4.936 121.401 6.313 121.401 8.176C121.401 10.039 119.943 11.416 118.053 11.416C116.163 11.416 114.732 10.039 114.732 8.176ZM134.55 9.769V6.988H130.554V0.831999H127.233V18.166H130.554V9.769H134.55ZM136.289 7.312C136.289 10.633 139.016 13.198 142.823 13.198C146.711 13.198 149.492 10.633 149.492 7.312C149.492 3.991 146.711 1.426 142.823 1.426C139.016 1.426 136.289 3.991 136.289 7.312ZM139.556 7.285C139.556 5.476 140.96 4.234 142.823 4.234C144.74 4.234 146.225 5.476 146.225 7.285C146.225 9.121 144.74 10.363 142.823 10.363C140.96 10.363 139.556 9.121 139.556 7.285ZM156.62 13.441V0.804999H153.299V13.441H156.62ZM156.998 26.266V23.512H145.064V21.946H156.62V14.683H141.797V17.464H153.353V19.165H141.797V26.266H156.998ZM160.383 9.148C160.383 13.279 163.245 16.033 167.16 16.033C171.129 16.033 173.964 13.279 173.964 9.148C173.964 4.99 171.129 2.236 167.16 2.236C163.245 2.236 160.383 4.99 160.383 9.148ZM163.677 9.121C163.677 6.691 165.162 5.152 167.16 5.152C169.185 5.152 170.643 6.691 170.643 9.121C170.643 11.578 169.185 13.09 167.16 13.09C165.162 13.09 163.677 11.578 163.677 9.121ZM177.393 23.107H180.66V0.831999H177.393V23.107ZM23.4804 47.256C23.4804 46.716 23.4264 46.203 23.3454 45.717H24.8844V48.822H23.3454C23.4264 48.336 23.4804 47.796 23.4804 47.256ZM14.5164 47.229C14.5164 44.799 15.7314 43.287 17.4054 43.287C19.1064 43.287 20.3484 44.799 20.3484 47.229C20.3484 49.686 19.1064 51.171 17.4054 51.171C15.7314 51.171 14.5164 49.686 14.5164 47.229ZM29.7444 61.134H33.0114V38.832H29.7444V61.134ZM28.0434 60.216V39.156H24.8844V42.666H22.0494C20.9694 41.262 19.3494 40.425 17.4054 40.425C13.8954 40.425 11.3574 43.179 11.3574 47.256C11.3574 51.306 13.8954 54.06 17.4054 54.06C19.3224 54.06 20.9424 53.25 22.0224 51.873H24.8844V60.216H28.0434ZM39.2053 45.717C39.2053 43.881 40.5823 42.531 42.4183 42.531C44.2543 42.531 45.6583 43.881 45.6583 45.717C45.6583 47.553 44.2543 48.957 42.4183 48.957C40.5823 48.957 39.2053 47.553 39.2053 45.717ZM55.3243 63.996V54.168H40.5283V56.922H52.0033V63.996H55.3243ZM58.8073 49.74V47.013H55.3243V44.637H58.8073V41.883H55.3243V38.805H52.0033V52.98H55.3243V49.74H58.8073ZM35.9383 45.771C35.9383 49.146 38.6383 51.738 42.4183 51.738C46.1983 51.738 48.8713 49.146 48.8713 45.771C48.8713 42.369 46.1983 39.723 42.4183 39.723C38.6383 39.723 35.9383 42.369 35.9383 45.771ZM87.4046 63.699V60.891H75.7946V56.733H72.5006V63.699H87.4046ZM81.4916 45.798V43.071H67.2626V45.798H81.4916ZM77.9276 41.991V39.21H71.1236V41.991H77.9276ZM77.0096 51.144C77.0096 52.197 76.0916 53.061 74.6336 53.061C73.0946 53.061 72.1766 52.197 72.1766 51.144C72.1766 50.037 73.0946 49.2 74.6336 49.2C76.0916 49.2 77.0096 50.037 77.0096 51.144ZM80.1686 51.117C80.1686 48.282 77.7386 46.554 74.6336 46.554C71.4476 46.554 68.9366 48.282 68.9366 51.117C68.9366 53.952 71.4476 55.599 74.6336 55.599C77.7116 55.599 80.1686 53.952 80.1686 51.117ZM90.6986 49.011V46.23H86.7296V38.832H83.3816V57.435H86.7296V49.011H90.6986ZM112.768 56.301V38.832H109.474V44.529H104.722V39.831H101.401V43.341H95.9475V39.831H92.6805V52.17H104.722V47.256H109.474V56.301H112.768ZM113.119 63.375V60.54H101.563V54.708H98.2155V63.375H113.119ZM101.401 49.389H95.9475V46.122H101.401V49.389ZM119.097 47.229C119.097 44.934 120.312 43.503 121.959 43.503C123.633 43.503 124.875 44.934 124.875 47.229C124.875 49.551 123.633 50.982 121.959 50.982C120.312 50.982 119.097 49.551 119.097 47.229ZM134.109 61.134H137.376V38.832H134.109V61.134ZM115.938 47.256C115.938 51.252 118.449 53.925 121.959 53.925C125.091 53.925 127.44 51.819 127.926 48.525H129.384V60.216H132.489V39.156H129.384V45.69H127.872C127.305 42.585 125.01 40.587 121.959 40.587C118.449 40.587 115.938 43.26 115.938 47.256ZM166.055 59.973V57.192H154.175V55.68H166.163V52.872H150.881V64.104H166.487V61.323H154.175V59.973H166.055ZM158.711 39.642V42.342H163.544C163.598 43.935 163.382 46.365 162.869 48.174H156.416C157.145 45.636 157.523 42.315 157.469 39.642H149.585V42.342H154.202C154.229 43.773 153.851 46.23 153.095 48.174H147.344V50.982H169.862V48.174H166.109C166.676 45.906 167 42.261 166.865 39.642H158.711ZM172.654 60.972H176.38V57.435H172.654V60.972ZM173.221 54.249H175.867L176.38 39.183H172.762L173.221 54.249Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
